/**
 * MemberPermissionsPanel — Iter63f
 *
 * RP37 (D4) — réglage FIN d'un projet, replié sous « Avancé » dans la fiche membre.
 * « Par défaut » = droits de la personne au niveau du cabinet (rôle + écran
 * Équipe MOE › Droits de l'équipe) ; un réglage ici l'emporte, pour ce projet seulement.
 *
 * Panneau de gestion des overrides de permissions pour un membre du chantier.
 *
 * Affiche 3 colonnes :
 *  - Permissions par défaut du rôle (lecture seule)
 *  - Modifications (toggles granted/revoked + note)
 *  - Permissions effectives (résultat live, lecture seule)
 *
 * Endpoints :
 *  - GET /api/chantiers/{cid}/equipe/{user_id}/permissions
 *  - PUT /api/chantiers/{cid}/equipe/{user_id}/permissions
 *  - DELETE /api/chantiers/{cid}/equipe/{user_id}/permissions
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { clearPermissionsCache } from "../../hooks/usePermissions";
import { libelleRoleProjet } from "../../lib/libelles";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Libellés humains des flags affichés (matrice iter63c-A — ajout des 3 modules sensibles).
// RP05 (fiche 02 §3/§7.2) — 4 cases retirées de l'affichage car elles n'ont AUCUN effet
// côté serveur (aucune route ne lit ces drapeaux) : « Voir les marges », « Voir les autres
// entreprises », « Voir les communications internes MOE », « Voir les CRC en brouillon ».
// Les clés restent dans la matrice serveur ; un override déjà enregistré sur l'une
// d'elles est conservé tel quel à l'enregistrement (granted/revoked non filtrés).
export const HIDDEN_NO_EFFECT_FLAGS = [
  "can_see_marges",
  "can_see_other_companies",
  "can_see_internal_communications",
  "can_see_crc_drafts",
];

export const FLAG_LABELS = {
  can_modify_team: "Modifier l'équipe",
  can_modify_planning: "Modifier le planning",
  can_modify_chantier_settings: "Modifier les paramètres du chantier",
  can_delete_chantier: "Supprimer le chantier",
  // RP36 — même droit que « Certifier les situations » (écran Droits de l'équipe)
  can_validate_documents: "Certifier les situations et valider les documents",
  can_see_finances: "Voir les finances (budget, marchés, factures)",
  can_see_recent_activity: "Voir l'activité récente",
  can_see_quick_actions: "Voir les actions rapides",
  can_create_crc: "Créer des comptes rendus",
  // RP19 / D14 — droit délégable : sans lui, le collaborateur « soumet à l'architecte ».
  can_diffuser_crc: "Peut diffuser un CR (valider et diffuser)",
  can_modify_dpgf: "Modifier le DPGF",
  // RP34 / D2 — engage le cabinet : architecte et chef de projet par défaut, délégable.
  can_lock_dpgf: "Verrouiller la DPGF",
  // RP36 / D2 — lancer, inviter, attribuer : engage le cabinet (délégable).
  can_manage_consultation: "Lancer et attribuer une consultation",
  can_modify_cctp: "Modifier le CCTP",
  can_modify_visite: "Faire des visites",
};

const FLAG_ORDER = Object.keys(FLAG_LABELS);

function _bool(v) {
  return v === true;
}

export default function MemberPermissionsPanel({ chantierId, userId, memberName }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null); // { role_projet, default_permissions, override, effective_permissions }
  const [granted, setGranted] = useState(new Set());
  const [revoked, setRevoked] = useState(new Set());
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!chantierId || !userId) return;
    setLoading(true);
    axios
      .get(`${API_URL}/api/chantiers/${chantierId}/equipe/${userId}/permissions`, { withCredentials: true })
      .then((r) => {
        if (!alive) return;
        setData(r.data);
        setGranted(new Set(r.data?.override?.granted || []));
        setRevoked(new Set(r.data?.override?.revoked || []));
        setNote(r.data?.override?.note || "");
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.detail || "Erreur de chargement");
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [chantierId, userId]);

  // Permissions effectives calculées en live (preview avant save)
  const effectiveLive = useMemo(() => {
    if (!data?.default_permissions) return {};
    const out = { ...data.default_permissions };
    for (const f of revoked) out[f] = false;
    for (const f of granted) out[f] = true;
    return out;
  }, [data, granted, revoked]);

  const hasChanges = useMemo(() => {
    const prevG = new Set(data?.override?.granted || []);
    const prevR = new Set(data?.override?.revoked || []);
    const prevNote = data?.override?.note || "";
    if (prevG.size !== granted.size || prevR.size !== revoked.size) return true;
    for (const f of prevG) if (!granted.has(f)) return true;
    for (const f of prevR) if (!revoked.has(f)) return true;
    if ((note || "") !== prevNote) return true;
    return false;
  }, [data, granted, revoked, note]);

  const cycleFlag = (flag) => {
    // Cycle : default → toggle (granted ou revoked selon defaut) → default
    const def = _bool(data?.default_permissions?.[flag]);
    const isGranted = granted.has(flag);
    const isRevoked = revoked.has(flag);
    const newG = new Set(granted);
    const newR = new Set(revoked);
    if (isGranted) {
      // granted → default
      newG.delete(flag);
    } else if (isRevoked) {
      // revoked → default
      newR.delete(flag);
    } else {
      // default → opposite override
      if (def) newR.add(flag);
      else newG.add(flag);
    }
    setGranted(newG);
    setRevoked(newR);
  };

  const overrideStateOf = (flag) => {
    if (granted.has(flag)) return "granted";
    if (revoked.has(flag)) return "revoked";
    return "default";
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.put(
        `${API_URL}/api/chantiers/${chantierId}/equipe/${userId}/permissions`,
        { granted: [...granted], revoked: [...revoked], note: note || null },
        { withCredentials: true }
      );
      setData(r.data);
      setGranted(new Set(r.data?.override?.granted || []));
      setRevoked(new Set(r.data?.override?.revoked || []));
      setNote(r.data?.override?.note || "");
      clearPermissionsCache(chantierId);
      toast.success("Permissions mises à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Réinitialiser aux permissions par défaut du rôle ?")) return;
    setSaving(true);
    try {
      const r = await axios.delete(
        `${API_URL}/api/chantiers/${chantierId}/equipe/${userId}/permissions`,
        { withCredentials: true }
      );
      setData(r.data);
      setGranted(new Set());
      setRevoked(new Set());
      setNote("");
      clearPermissionsCache(chantierId);
      toast.success("Permissions réinitialisées");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de réinitialisation");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-[#6B6B6B] py-4" data-testid="member-perms-loading">Chargement…</div>;
  }
  if (error) {
    return <div className="text-sm text-[#8B3A3A] py-4" data-testid="member-perms-error">{error}</div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-3" data-testid="member-permissions-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-[#6B6B6B]">Rôle sur le chantier</div>
          <div className="font-medium text-[#2C2C2C]">{data.role_projet ? libelleRoleProjet(data.role_projet) : "—"}</div>
        </div>
        {data.override && [...(data.override.granted || []), ...(data.override.revoked || [])].some((f) => FLAG_LABELS[f]) ? (
          <div className="text-xs px-2 py-0.5 bg-amber-100 text-amber-800 rounded-sm" data-testid="member-perms-modified-badge">
            modifié
          </div>
        ) : null}
      </div>

      <p className="text-xs text-[#6B6B6B]" data-testid="member-perms-par-defaut-aide">
        « Par défaut » : droits de la personne dans le cabinet (Équipe MOE › Droits de l'équipe).
      </p>
      <div className="grid grid-cols-12 gap-2 text-xs uppercase tracking-wide text-[#6B6B6B] border-b border-[#E5E5E5] pb-1">
        <div className="col-span-6">Droit</div>
        <div className="col-span-2 text-center">Par défaut</div>
        <div className="col-span-2 text-center">Ajusté</div>
        <div className="col-span-2 text-center">Autorisé</div>
      </div>

      <div className="space-y-1">
        {FLAG_ORDER.map((flag) => {
          const def = _bool(data.default_permissions?.[flag]);
          const eff = _bool(effectiveLive?.[flag]);
          const state = overrideStateOf(flag);
          let modPill = null;
          if (state === "granted") {
            modPill = <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded-sm">+ droit confié</span>;
          } else if (state === "revoked") {
            modPill = <span className="text-[10px] px-1.5 py-0.5 bg-rose-100 text-rose-800 rounded-sm">- droit retiré</span>;
          } else {
            modPill = <span className="text-[10px] text-[#999]">—</span>;
          }
          return (
            <button
              key={flag}
              type="button"
              onClick={() => cycleFlag(flag)}
              className="grid grid-cols-12 gap-2 items-center w-full text-left px-1 py-1 hover:bg-[#F8F6F2] rounded-sm"
              data-testid={`perm-row-${flag}`}
              title="Cliquer pour confier ou retirer ce droit (un second clic rétablit le réglage par défaut)"
            >
              <div className="col-span-6 text-sm text-[#2C2C2C]">{FLAG_LABELS[flag]}</div>
              <div className="col-span-2 text-center text-xs">
                <span className={def ? "text-emerald-700" : "text-[#999]"}>{def ? "✓" : "—"}</span>
              </div>
              <div className="col-span-2 text-center">{modPill}</div>
              <div className="col-span-2 text-center text-sm font-medium">
                <span className={eff ? "text-emerald-700" : "text-[#8B3A3A]"} data-testid={`perm-effective-${flag}`}>
                  {eff ? "✓" : "✕"}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <div>
        <Label className="text-xs">Note interne (optionnelle)</Label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          className="w-full mt-1 border border-[#E5E5E5] rounded-none p-2 text-sm focus:outline-none focus:border-[#2C2C2C]"
          placeholder="Ex : promotion temporaire pour la phase consultation"
          data-testid="member-perms-note"
        />
      </div>

      <div className="flex justify-between gap-2 pt-2">
        <Button
          variant="outline"
          onClick={handleReset}
          disabled={saving || (!data.override?.granted?.length && !data.override?.revoked?.length)}
          className="rounded-none text-[#8B3A3A] border-[#8B3A3A] hover:bg-rose-50"
          data-testid="member-perms-reset"
        >
          Réinitialiser
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none"
          data-testid="member-perms-save"
        >
          {saving ? "Enregistrement…" : "Enregistrer permissions"}
        </Button>
      </div>
    </div>
  );
}
